import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// Tipos para Igreja
export interface Igreja {
  id: string
  nome: string
  endereco: string | null
  cidade: string | null
  estado: string | null
  cep: string | null
  telefone: string | null
  email: string | null
  pastor: string | null
  descricao: string | null
  dominio: string | null
  ativa: boolean | null
  cor_primaria: string | null
  cor_secundaria: string | null
  logo_light: string | null
  logo_dark: string | null
  criado_em: string | null
  atualizado_em: string | null
}

// Tipos para Usuário com Permissões
export type TipoPermissao = 'admin' | 'gestor' | 'lider' | 'membro' | null

export interface UsuarioAdmin {
  id: string
  nome: string
  email: string
  telefone: string | null
  endereco: string | null
  igreja_id: string | null
  permissao: TipoPermissao
  is_admin: boolean | null
  ativo: boolean | null
  criado_em: string | null
  atualizado_em: string | null
  igreja?: {
    id: string
    nome: string
  } | undefined
}

// Hook para buscar igrejas
export function useIgrejas() {
  return useQuery({
    queryKey: ['igrejas'],
    queryFn: async (): Promise<Igreja[]> => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('igrejas')
        .select('*')
        .order('nome')

      if (error) {
        throw new Error(`Erro ao buscar igrejas: ${error.message}`)
      }

      return data || []
    },
  })
}

// Hook para buscar usuários com informações da igreja
export function useUsuarios() {
  return useQuery({
    queryKey: ['usuarios-admin'],
    queryFn: async (): Promise<UsuarioAdmin[]> => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('usuarios')
        .select(`
          *,
          igreja:igrejas(id, nome)
        `)
        .order('nome')

      if (error) {
        throw new Error(`Erro ao buscar usuários: ${error.message}`)
      }

      return (data || []).map(item => ({
        ...item,
        igreja: item.igreja || undefined
      })) as UsuarioAdmin[]
    },
  })
}

// Hook para criar nova igreja
export function useCreateIgreja() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (igreja: Omit<Igreja, 'id' | 'criado_em' | 'atualizado_em'>) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('igrejas')
        .insert([igreja])
        .select()
        .single()

      if (error) {
        throw new Error(`Erro ao criar igreja: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['igrejas'] })
    },
  })
}

// Hook para atualizar igreja
export function useUpdateIgreja() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Igreja> & { id: string }) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('igrejas')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw new Error(`Erro ao atualizar igreja: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['igrejas'] })
    },
  })
}

// Hook para deletar igreja
export function useDeleteIgreja() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { error } = await supabase
        .from('igrejas')
        .delete()
        .eq('id', id)

      if (error) {
        throw new Error(`Erro ao deletar igreja: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['igrejas'] })
    },
  })
}

// Hook para criar novo usuário
export function useCreateUsuario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (usuario: Omit<UsuarioAdmin, 'id' | 'criado_em' | 'atualizado_em' | 'igreja'>) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('usuarios')
        .insert([usuario])
        .select()
        .single()

      if (error) {
        throw new Error(`Erro ao criar usuário: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-admin'] })
    },
  })
}

// Hook para atualizar usuário
export function useUpdateUsuario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<UsuarioAdmin> & { id: string }) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Remove a propriedade igreja do updates pois não é um campo da tabela
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { igreja, ...userUpdates } = updates

      const { data, error } = await supabase
        .from('usuarios')
        .update(userUpdates)
        .eq('id', id)
        .select(`
          *,
          igreja:igrejas(id, nome)
        `)
        .single()

      if (error) {
        throw new Error(`Erro ao atualizar usuário: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-admin'] })
    },
  })
}

// Hook para deletar usuário
export function useDeleteUsuario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { error } = await supabase
        .from('usuarios')
        .delete()
        .eq('id', id)

      if (error) {
        throw new Error(`Erro ao deletar usuário: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-admin'] })
    },
  })
}

// Hook principal que combina todos os hooks
export function useAdmin() {
  const igrejas = useIgrejas()
  const usuarios = useUsuarios()
  const createIgreja = useCreateIgreja()
  const updateIgreja = useUpdateIgreja()
  const deleteIgreja = useDeleteIgreja()
  const createUsuario = useCreateUsuario()
  const updateUsuario = useUpdateUsuario()
  const deleteUsuario = useDeleteUsuario()

  return {
    // Queries
    igrejas: igrejas.data || [],
    usuarios: usuarios.data || [],
    isLoadingIgrejas: igrejas.isLoading,
    isLoadingUsuarios: usuarios.isLoading,
    errorIgrejas: igrejas.error,
    errorUsuarios: usuarios.error,

    // Mutations
    createIgreja: createIgreja.mutate,
    updateIgreja: updateIgreja.mutate,
    deleteIgreja: deleteIgreja.mutate,
    createUsuario: createUsuario.mutate,
    updateUsuario: updateUsuario.mutate,
    deleteUsuario: deleteUsuario.mutate,

    // Loading states
    isCreatingIgreja: createIgreja.isPending,
    isUpdatingIgreja: updateIgreja.isPending,
    isDeletingIgreja: deleteIgreja.isPending,
    isCreatingUsuario: createUsuario.isPending,
    isUpdatingUsuario: updateUsuario.isPending,
    isDeletingUsuario: deleteUsuario.isPending,
  }
}
